import React, { useEffect, useState } from 'react';
import {
  Box,
  makeStyles,
  CircularProgress,
  Divider,
  Button,
  Typography,
  Link
} from '@material-ui/core';
import { isArray, map, isEmpty, omit, get } from 'lodash';
import clsx from 'clsx';
import qs from 'qs';
import Page from '../../../../components/Page';
import AbyssLeaderboardTable from './AbyssLeaderboardTable';
import AbyssLeaderboardFilters from './AbyssLeaderboardFilters';
import AbyssLeaderboardBanner from './AbyssLeaderboardBanner';
import { CURRENT_ABYSS_VERSION } from '../../../../constants';
import SpeedrunLeaderboardAPI from '../../../../api/SpeedrunLeaderboard';
import SpeedrunLeaderboardSpotsAPI from '../../../../api/SpeedrunLeaderboardSpot';
import YoutubePlayerModal from '../../../../components/YoutubePlayerModal';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const useStyles = makeStyles(theme => ({
  pageContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: theme.palette.primary.hover,
    flexGrow: 1
  },
  tableContainer: {
    fiex: 1,
    overflow: 'auto',
    width: '55%',
    minWidth: '1200px',
    alignSelf: 'center',
    backgroundColor: theme.palette.secondary.secondary,
    border: '1px solid #ced3d3',
    boxShadow: '0px 0px 1px 1px #cdd2d2'
  },
  tableHeader: {
    width: '55%',
    minWidth: '1200px',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    marginBottom: '0.5rem',
    marginTop: '0.5rem',
    alignSelf: 'center'
  },
  loading: {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    aspectRatio: 1,
    display: 'grid',
    placeItems: 'center',
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.2)',
    color: theme.palette.primary.main,
    zIndex: 3
  },
  doneLoading: {
    display: 'none'
  },
  banner: {
    width: '100%',
    background: 'none'
  },
  bannerBackground: {
    width: '100%',
    background: 'none',
    position: 'absolute',
    top: '20%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  },
  bannerOverlay: {
    color: theme.palette.text.secondary,
    marginTop: '2rem',
    width: '80%',
    alignSelf: 'center',
    textAlign: 'center',
    zIndex: 1
  },
  bannerRules: {
    borderRadius: '5px',
    background: 'rgba(0, 0, 0, 0.5)',
    width: '80%',
    alignSelf: 'center',
    color: theme.palette.text.secondary,
    marginTop: '2rem',
    zIndex: 1
  },
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    '&hover': {
      color: theme.palette.text.primary
    }
  },
  characterSideBar: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  characterImage: {
    width: '4rem !important',
    height: '4rem !important',
    backgroundColor: theme.palette.text.primary,
    margin: '0.5rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.text.secondary
    }
  },
  alert: {
    width: '55%',
    minWidth: '1200px',
    backgroundColor: `${theme.palette.secondary.main}40`,
    color: theme.palette.background.dark
  },
  alertIcon: {
    color: 'black !important'
  },
  videoModal: {
    borderRadius: 0,
    boxShadow: '0px 0px 1px 1px #cdd2d2',
    padding: 0,
    width: '800px',
    height: '700px',
    overflow: 'auto'
  },
  videoModalHeader: {
    color: theme.palette.tertiary.secondary,
    fontWeight: '600',
    width: '100%',
    height: 'fit-content',
    // backgroundColor: 'white',
    border: '1px solid #cdd2d2',
    marginBottom: '1rem'
  },
  youtubePlayer: {
    width: '650px',
    height: '450px',
    boxShadow: 'inset 0 0 10px black',
    borderRadius: 10
  },
  youtubePlayerContainer: {
    width: 'fit-content',
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1
  },
  teamDisplay: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  viewProfileBtn: {
    backgroundColor: '#f2f2f2',
    borderRadius: 10,
    color: `${theme.palette.background.dark} !important`,
    '&:hover': {
      backgroundColor: '#ced3d3'
    },
    marginLeft: '1rem'
  },
  characterImageVideoPreview: {
    backgroundColor: '#ebeef1',
    boxShadow: '0px 0px 1px 1px #cdd2d2',
    border: '1px solid #cdd2d2',
    height: '40px',
    width: '40px',
    margin: '0.1rem'
  },
  text: {
    fontSize: '12px',
    color: '#8c96a2'
  },
  highTier: {
    color: '#e29205 !important'
  },
  midTier: {
    color: '#1f8ecf !important'
  },
  lowTier: {
    color: '#2daf7f !important'
  },
  noTier: {
    color: '#737373 !important'
  },
  important: {
    fontWeight: '600',
    fontSize: '16px',
    color: theme.palette.background.dark
  }
}));

function AbyssLeaderboardView(props) {
  const classes = useStyles();
  const [params, setParams] = useState({
    limit: 25,
    page: 0,
    speedrun_category: 'Abyss',
    abyss_version: CURRENT_ABYSS_VERSION,
    days_elapsed: 1,
    sortBy: 'rank',
    sortDir: 'asc'
  });
  const [leaderboard, setLeaderboard] = useState({});
  const [entries, setEntries] = useState([]);
  const [userProfileInformations, setUserProfileInformations] = useState([]);
  const [runAssets, setRunAssets] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [videoModalContent, setVideoModalContent] = useState({});
  const [videoOpen, setVideoOpen] = useState(false);

  /**
   * @description This function is used to retrieve asynchronously the leaderboard entries
   * sets loading to true when fetching request, once request promise resolved, sets loading
   * to false
   */
  async function getEntries() {
    setLoading(true);

    const response = await SpeedrunLeaderboardAPI.getSpeedrunLeaderboardWithInstance(
      {
        speedrun_category: params.speedrun_category,
        abyss_version: params.abyss_version
      }
    ).catch(err => {
      setEntries([]);
      setUserProfileInformations([]);
      setRunAssets([]);

      setCount(0);
      setLoading(false);
    });

    props.history.push({ search: qs.stringify({ ...params }) });

    setLeaderboard(response.speedrunLeaderboardWithInstance);

    const entryResponse = await SpeedrunLeaderboardSpotsAPI.getAllWithHistoryDetailed(
      {
        ...omit(params, ['speedrun_category', 'abyss_version']),
        instance_id: get(
          response.speedrunLeaderboardWithInstance,
          'instance_id'
        )
      }
    ).catch(err => {
      setEntries([]);
      setUserProfileInformations([]);
      setRunAssets([]);

      setCount(0);
      setLoading(false);
    });

    setEntries(entryResponse.speedrunLeaderboardSpots.rows);
    setUserProfileInformations(entryResponse.userProfileInformations);
    setRunAssets(entryResponse.runAssets);

    setCount(entryResponse.speedrunLeaderboardSpots.count);
    setLoading(false);
  }

  /**
   * @description Runs on change of query parameters
   */
  useEffect(() => {
    if (isEmpty(params)) return;

    getEntries().catch(console.error);
  }, [params, props.history]);

  /**
   * @description Handles changing the page number of the table
   * @param {any} event - the Synthetic DOM event
   * @param {number} newPage - new page number
   */
  function handlePageChange(event, newPage) {
    setParams(p => ({
      ...p,
      page: newPage
    }));
  }

  /**
   * @description Handles changing the page size of the table
   * @param {any} event - the Synthetic DOM event
   */
  function handleLimitChange(event) {
    setParams(p => ({
      ...p,
      limit: event.target.value
    }));
  }

  /**
   * @description Handles the sorting of all columns in the table
   * @param {string} col - table column name
   */
  function sortByColumn(col) {
    const reverseOrder = params.sortDir === 'desc' ? 'asc' : 'desc';

    setParams(p => ({
      ...p,
      page: 0,
      sortBy: col,
      sortDir: params && params.sortBy === col ? reverseOrder : 'desc'
    }));
  }

  /**
   * @description Sets the filter by the filter key and values to filter
   * @param {string} key - id to filter by
   * @param {array|string} search - either value or array of values to filter by
   */
  function setFilter(key, search) {
    setParams(params => {
      const newParams = {
        ...params,
        page: 0
      };

      if (!search || (isArray(search) && isEmpty(search)))
        return omit(newParams, key);

      return {
        ...newParams,
        [key]: isArray(search) ? map(search, 'value') : search.value
      };
    });
  }

  /**
   * @description Sets the current abyss version
   * @param {string} version - The version to update
   */
  function setVersion(version) {
    setParams(params => ({
      ...params,
      abyss_version: version
    }));
  }

  /**
   * @description Sets video on video modal, and opens video modal
   */
  function loadVideo(name, entry) {
    return () => {
      setVideoModalContent({
        url: entry.video_link,
        name,
        rank: entry.rank
      });

      setVideoOpen(true);
    };
  }

  /**
   * @description Closes the video modal
   */
  function handleCloseVideo() {
    setVideoModalContent({});
    setVideoOpen(false);
  }

  const tableHeaders = [
    {
      label: '#',
      id: 'rank'
    },
    {
      label: '',
      id: 'change'
    },
    {
      label: 'Name',
      id: 'competitor'
    },
    {
      label: 'Region',
      id: 'region'
    },
    {
      label: 'Pts.',
      id: 'points'
    },
    {
      label: 'Most Used',
      id: 'most_used'
    },
    {
      label: 'Breakdown',
      id: 'breakdown'
    }
  ];

  const entriesParsed = map(entries, entry => {
    const { time } = entry;
    const minutes = Math.floor(time / 60000);
    const seconds = (time % 60000) / 1000;
    return {
      ...entry,
      minutes,
      seconds
    };
  });

  return (
    <Page {...props}>
      <Box classes={{ root: classes.pageContainer }}>
        <AbyssLeaderboardBanner
          leaderboard={leaderboard}
          version={params.abyss_version}
          updateVersion={setVersion}
        />
        <Box mt={2} />
        <Box className={classes.tableHeader}>
          <AbyssLeaderboardFilters
            setFilter={setFilter}
            page={params.page}
            limit={params.limit}
            count={count}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleLimitChange}
            defaultValues={{
              abyss_version: params.abyss_version,
              speedrun_subcategory: params.speedrun_subcategory
            }}
          />
        </Box>
        <Box className={classes.tableContainer}>
          <Box
            className={clsx({
              [classes.loading]: isLoading,
              [classes.doneLoading]: !isLoading
            })}
          >
            <CircularProgress color="inherit" />
          </Box>
          <AbyssLeaderboardTable
            headers={tableHeaders}
            rows={entriesParsed}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            onClickSort={sortByColumn}
            details={userProfileInformations}
            assets={runAssets}
            loadVideo={loadVideo}
          />
        </Box>
        <Box mt={2} />
      </Box>
      {!isEmpty(videoModalContent) && (
        <YoutubePlayerModal
          open={videoOpen}
          handleClose={handleCloseVideo}
          classes={{ root: classes.videoModal }}
          headerComponent={
            <Box classes={{ root: classes.videoModalHeader }}>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100%',
                  paddingTop: '2rem',
                  paddingLeft: '2rem',
                  paddingRight: '2rem',
                  paddingBottom: '1rem'
                }}
              >
                <Typography
                  align="center"
                  style={{ fontWeight: '700', fontSize: '20px' }}
                >
                  {videoModalContent.name}
                </Typography>
                <Typography
                  style={{ marginLeft: '1rem' }}
                  classes={{
                    root: clsx({
                      [classes.text]: true,
                      [classes.important]: true,
                      [classes.highTier]: videoModalContent.rank <= 5,
                      [classes.midTier]:
                        5 < videoModalContent.rank &&
                        videoModalContent.rank <= 15,
                      [classes.lowTier]:
                        15 < videoModalContent.rank &&
                        videoModalContent.rank <= 30,
                      [classes.noTier]: 30 < videoModalContent.rank
                    })
                  }}
                >
                  {videoModalContent.rank}
                </Typography>
                <EmojiEventsIcon />
                <Button
                  classes={{ root: clsx(classes.viewProfileBtn, classes.text) }}
                  endIcon={<ChevronRightIcon />}
                  onClick={() =>
                    props.history.push(
                      `/public-profile?alias=${videoModalContent.name}`
                    )
                  }
                >
                  View Profile
                </Button>
                <CloseIcon
                  onClick={handleCloseVideo}
                  style={{ marginLeft: 'auto', cursor: 'pointer' }}
                />
              </Box>
              <Divider />
            </Box>
          }
          footerComponent={
            <Box mt={2}>
              <Link
                target="_blank"
                rel="noopener"
                href={videoModalContent.url}
                color="inherit"
                underline="hover"
              >
                <Typography classes={{ root: classes.text }}>
                  If the video isn't loading, click here
                </Typography>
              </Link>
            </Box>
          }
          videoLink={videoModalContent.url}
          YoutubePlayerProps={{
            classes: {
              root: classes.youtubePlayer,
              playerContainer: classes.youtubePlayerContainer
            }
          }}
        />
      )}
    </Page>
  );
}

export default AbyssLeaderboardView;
